/**
 * Khung chụp ảnh bằng webcam — cho ô ảnh mà mẫu đơn chỉ cho «Chụp ảnh» (`allowed_sources = ['camera']`)
 * khi người dùng đang ở MÁY BÀN.
 *
 * Vì sao cần: máy bàn BỎ QUA thuộc tính `capture` của <input type="file"> ⇒ nút "chụp" chỉ mở hộp chọn
 * tệp, tức chọn được ảnh có sẵn — đúng thứ chủ mẫu muốn chặn (minh chứng phải chụp tại chỗ). Điện thoại
 * và máy tính bảng thì tôn trọng `capture` (mở thẳng camera của máy, ảnh nét hơn) nên không đi đường này.
 *
 * Ảnh ra: JPEG, cạnh dài tối đa 1600px, chất lượng 0,8 — cùng cỡ app điện thoại gửi (image_picker
 * maxWidth/maxHeight 1600). Tắt camera ngay khi đóng khung (đèn camera không được sáng mãi).
 */
import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import Modal from './Modal';

const MAX_EDGE = 1600;

export default function CameraCaptureModal({ isOpen, onClose, onCapture }) {
  const { t } = useLingui();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setReady(false);
    setError('');
    const stop = () => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
    // http (không phải https/localhost) hoặc trình duyệt cũ: không có mediaDevices.
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(t`Trình duyệt này không mở được camera. Mẫu đơn chỉ nhận ảnh chụp trực tiếp — hãy gửi đơn từ điện thoại.`);
      return undefined;
    }
    navigator.mediaDevices.getUserMedia({
      // `ideal`, KHÔNG `exact`: máy bàn thường chỉ có camera trước — đòi đúng camera sau là hỏng.
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false,
    }).then((stream) => {
      if (cancelled) { stream.getTracks().forEach((tr) => tr.stop()); return; }
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        v.play().catch(() => {});
      }
    }).catch((e) => {
      if (cancelled) return;
      const name = e?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError(t`Chưa được phép dùng camera — hãy cho phép trình duyệt truy cập camera rồi thử lại.`);
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setError(t`Máy này không có camera. Mẫu đơn chỉ nhận ảnh chụp trực tiếp — hãy gửi đơn từ điện thoại.`);
      } else if (name === 'NotReadableError') {
        setError(t`Camera đang bị ứng dụng khác dùng — hãy tắt ứng dụng đó rồi thử lại.`);
      } else {
        setError(t`Không mở được camera — thử lại, hoặc gửi đơn từ điện thoại.`);
      }
    });
    return () => { cancelled = true; stop(); };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- `t` đổi danh tính mỗi lượt vẽ ⇒ vòng lặp

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return;
    const scale = Math.min(1, MAX_EDGE / Math.max(v.videoWidth, v.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { setError(t`Không chụp được ảnh — thử lại.`); return; }
      onCapture?.(new File([blob], `anh-chup-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.8);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t`Chụp ảnh`} size="lg">
      {error ? (
        <p className="text-sm text-red-600 py-6 text-center">{error}</p>
      ) : (
        <div className="relative bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center">
          <video ref={videoRef} autoPlay playsInline muted onLoadedData={() => setReady(true)}
            className="w-full h-full object-contain" />
          {!ready && <Loader2 className="absolute w-6 h-6 text-white animate-spin" />}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
          <Trans>Huỷ</Trans>
        </button>
        {!error && (
          <button type="button" onClick={snap} disabled={!ready}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            <Camera size={14} /> <Trans context="mở máy ảnh để chụp">Chụp</Trans>
          </button>
        )}
      </div>
    </Modal>
  );
}
